import { computed, ref, Ref } from 'vue';
import { defineStore } from 'pinia';

import {
	BoardViewType, BoardColumnType, CardType,
	CardCommentType, BoardConfigurationType,
	BoardState, UserType, CardReactionType, BoardShareMode
} from '@/common/interfaces';

import { Column, Configuration } from '../board';
import Card from '../card';
import { BoardEventBus } from '@/app/BoardEventBus';

export declare type BoardIdentifier = string;

export const useBoardEditStore = defineStore('boardEdit', () => {
	const consumerChannel: Ref<string | null> = ref(null);

	const name: Ref<string> = ref('');
	const emoji: Ref<string | null> = ref(null);
	const state: Ref<BoardState> = ref(BoardState.DRAFT);
	const columns: Ref<Array<Column>> = ref([]);
	const cards: Ref<Map<string, Card>> = ref(new Map());
	const info: Ref<BoardViewType | null> = ref(null);
	const configuration: Ref<Configuration | null> = ref(null);
	const members: Ref<Map<string, UserType>> = ref(new Map());
	const participants: Ref<Map<string, UserType>> = ref(new Map());
	const shareMode: Ref<BoardShareMode> = ref(BoardShareMode.RESTRICTED);

	const loaded: Ref<boolean> = ref(false);

	function clearStore() {
		name.value = '';
		columns.value = [];
		info.value = null;
		emoji.value = null;
		loaded.value = false;
		configuration.value = null;
		shareMode.value = BoardShareMode.RESTRICTED;
		state.value = BoardState.DRAFT;

		cards.value.clear();
		members.value.clear();
		participants.value.clear();
	}

	function setStore(
		board: BoardViewType,
		setColumns: Array<BoardColumnType>,
		setCards: Array<CardType>
	) {
		clearStore();
		info.value = board;
		name.value = board.name;
		emoji.value = board.emoji;
		state.value = board.state;
		shareMode.value = board.shareMode;
		configuration.value = new Configuration(board.configuration);

		for (const col of setColumns) {
			const convertedColumn = new Column(col);
			columns.value.splice(convertedColumn.index, 0, convertedColumn);
		}

		for (const card of setCards) {
			const col = getColumnByUuid(card.column.uuid);

			if (col === null) continue;

			cards.value.set(card.uuid, new Card(card, col));
		}

		for (const member of board.members) {
			members.value.set(member.uuid, member);
		}

		for (const participant of board.participants) {
			participants.value.set(participant.uuid, participant);
		}

		loaded.value = true;
	}

	function setBoardName(newName: string) {
		if (info.value === null) {
			return;
		}

		info.value.name = newName;

		name.value = newName;
	}

	function setBoardEmoji(newEmoji: string | null) {
		if (info.value === null) {
			return;
		}

		info.value.emoji = newEmoji;

		emoji.value = newEmoji;
	}

	function setState(newState: BoardState) {
		if (info.value === null) {
			return;
		}

		info.value.state = newState;

		state.value = newState;
	}

	function setShareMode(newMode: BoardShareMode) {
		if (info.value === null) {
			return;
		}

		info.value.shareMode = newMode;
		shareMode.value = newMode;
	}

	function addColumn(newColumn: BoardColumnType) {
		const convertedColumn = new Column(newColumn);
		columns.value.splice(
			convertedColumn.index,
			0,
			convertedColumn
		);
		reindexColumns();
		BoardEventBus.emit('columns:changed');
	}

	function reindexColumns(): void {
		let i = 0;
		for (const col of columns.value) {
			col.index = i;
			i += 1;
		}
	}

	function removeColumn(columnUuid: string) {
		const index = columns.value.findIndex(
			(sc: Column) => sc.uuid === columnUuid
		);

		if (index > -1) {
			columns.value.splice(index, 1);
		}

		reindexColumns();
		BoardEventBus.emit('columns:changed');
	}

	function setColumnName(columnUuid: string, newName: string) {
		const column = columns.value.find(
			(sc: Column) => sc.uuid === columnUuid
		);

		if (column === undefined) {
			return;
		}

		column.name = newName;
	}

	function setColumnEmoji(columnUuid: string, newEmoji: string | null) {
		const column = columns.value.find(
			(sc: Column) => sc.uuid === columnUuid
		);

		if (column === undefined) {
			return;
		}

		column.emoji = newEmoji;
	}

	function setColumnOrder(columnsOrdered: Array<string>) {
		const newOrder: Array<Column> = [];

		let index = 0;
		for (const colUuid of columnsOrdered) {
			const column = columns.value.find(
				(c: Column) => c.uuid === colUuid
			);

			if (column) {
				newOrder.push(column);
				column.index = index;
				index++;
			}
		}

		columns.value = newOrder;
	}

	function setColumnParticipantsCanAddCards(columnUuid: string, newValue: boolean): void {
		const column = columns.value.find(
			(sc: Column) => sc.uuid === columnUuid
		);

		if (column === undefined) {
			return;
		}

		column.participantsCanAddCards = newValue;
	}

	function resetCards(newCards: Array<CardType>) {
		cards.value.clear();

		for (const card of newCards) {
			const col = getColumnByUuid(card.column.uuid);

			if (col === null) continue;

			cards.value.set(card.uuid, new Card(card, col));
		}
	}

	function addCard(card: CardType) {
		const col = getColumnByUuid(card.column.uuid);

		if (col === null) {
			return;
		}

		// This makes the assumption of the current behavior which is that
		//	all new cards go to the top aka index 0
		const columnCards = Array.from(cards.value.values()).filter(
			(c: Card) => c.column.uuid === card.column.uuid && c.uuid !== card.uuid
		).sort(
			(a: Card, b: Card) => a.index - b.index
		);

		for (const columnCard of columnCards) {
			columnCard.index += 1;
		}

		cards.value.set(card.uuid, new Card(card, col));
	}

	function updateCard(cardInfo: CardType) {
		const card = getCardByUuid(cardInfo.uuid);

		if (card === null) {
			return;
		}

		card.update(cardInfo);
	}

	function removeCard(cardUuid: string) {
		cards.value.delete(cardUuid);
	}

	function addCardVote(cardUuid: string, voterUuid: string, voteDateStr: string): void {
		const card = getCardByUuid(cardUuid);

		if (card === null) {
			return;
		}

		card.addVote(voterUuid, voteDateStr);
	}

	function removeCardVote(cardUuid: string, voterUuid: string): void {
		const card = getCardByUuid(cardUuid);

		if (card === null) {
			return;
		}

		card.removeVote(voterUuid);
	}

	function removeCardLinkPreview(cardUuid: string, linkPreviewId: number): void {
		const card = getCardByUuid(cardUuid);

		if (card === null) {
			return;
		}

		card.removeLinkPreview(linkPreviewId);
	}

	function addCardComment(comment: CardCommentType): void {
		const card = getCardByUuid(comment.cardUuid);

		if (card === null) {
			return;
		}

		card.addComment(comment);
	}

	function removeCardComment(cardUuid: string, commentUuid: string): void {
		const card = getCardByUuid(cardUuid);

		if (card === null) {
			return;
		}

		card.removeComment(commentUuid);
	}

	function updateCardComment(updatedComment: CardCommentType): void {
		const card = getCardByUuid(updatedComment.cardUuid);

		if (card === null) {
			return;
		}

		card.updateComment(updatedComment);
	}

	function addCardReaction(cardUuid: string, reaction: CardReactionType): void {
		const card = getCardByUuid(cardUuid);

		if (card === null) {
			return;
		}

		card.addReaction(reaction);
	}

	function removeCardReaction(cardUuid: string, emoji: string, userUuid: string): void {
		const card = getCardByUuid(cardUuid);

		if (card === null) {
			return;
		}

		card.removeReaction(emoji, userUuid);
	}

	function changeCardColumn(cardUuid: string, newColumnUuid: string, atIndex: number): void {
		const card = getCardByUuid(cardUuid);
		const column = getColumnByUuid(newColumnUuid);

		if (card === null || column === null) {
			return;
		}

		card.changeColumn(column);
		changeCardIndex(cardUuid, atIndex);
	}

	function toggleCardPinned(cardUuid: string, pinValue: boolean): void {
		const card = getCardByUuid(cardUuid);

		if (card === null) {
			return;
		}

		card.pinned = pinValue;
	}

	function changeCardIndex(cardUuid: string, toIndex: number): void {
		const card = getCardByUuid(cardUuid);

		if (card === null) {
			return;
		}

		const columnCards = Array.from(cards.value.values()).filter(
			(c: Card) => c.column.uuid === card.column.uuid && c.uuid !== card.uuid
		).sort(
			(a: Card, b: Card) => a.index - b.index
		);

		let index = 0;
		let added = false;
		for (const columnCard of columnCards) {
			if (index === toIndex) {
				card.index = index;
				index += 1;
				added = true;
			}
			columnCard.index = index;
			index += 1;
		}
		if (!added) {
			card.index = index;
		}
	}

	const getCardByUuid = (cardUuid: string): Card | null => {
		const card = cards.value.get(cardUuid);

		if (card === undefined) {
			return null;
		}

		return card;
	};

	const getColumnByUuid = (columnUuid: string): Column | null => {
		const col = columns.value.find(
			(c: Column) => c.uuid === columnUuid
		);

		if (col === undefined) {
			return null;
		}

		return col;
	};

	const updateConfiguration = (newConfiguration: BoardConfigurationType): void => {
		if (configuration.value === null) {
			configuration.value = new Configuration(newConfiguration);
		} else {
			configuration.value.update(newConfiguration);
		}
	}

	const cardsByUser = computed(
		() => {
			const result: Map<string, Array<Card>> = new Map();

			for (const card of cards.value.values()) {
				if (card.creator && !result.has(card.creator.uuid)) {
					result.set(card.creator.uuid, []);
				}

				if (card.creator) {
					result.get(card.creator.uuid)!.push(card);
				}
			}

			return result;
		}
	);

	const votesByUser = computed(
		() => {
			const result: Map<string, number> = new Map();

			for (const card of cards.value.values()) {
				for (const vote of card.votes) {
					if (!result.has(vote[0])) {
						result.set(vote[0], 0);
					}

					result.set(vote[0], result.get(vote[0])!+1);
				}
			}

			return result;
		}
	);

	const setConsumerChannel = (channel: string): void => {
		consumerChannel.value = channel;
	}

	return {
		name, emoji, columns, info, cards, configuration, state, members, participants,
		shareMode,
		clearStore, setStore, setBoardName, setBoardEmoji, setState,
		setShareMode,
		addColumn, removeColumn, setColumnName, setColumnEmoji,
		setColumnParticipantsCanAddCards,
		getColumnByUuid, setColumnOrder,
		addCard, removeCard, updateCard, getCardByUuid, resetCards,
		addCardVote, removeCardVote, addCardComment, removeCardComment,
		removeCardLinkPreview, toggleCardPinned,
		updateCardComment, updateConfiguration, addCardReaction, removeCardReaction,
		cardsByUser, votesByUser, changeCardColumn, changeCardIndex,
		setConsumerChannel, consumerChannel
	};
});
